<script setup lang="ts">
import type { components as ContentSchemas } from '@core/types/content-shapes'
import type { ContentBlock, ContentDynamicRendererProps } from '@nimbus/types'
import type { ContentVendorListData } from '@nimbus/types/components/ContentVendorGames'
import { computed, useFaqSchema, provideDeferredMedia } from '#imports'

const props = defineProps<ContentDynamicRendererProps>()

// Tells the images in this content to wait for mount instead of loading from the SSR HTML.
provideDeferredMedia(Boolean(props.deferMedia))

const { addFaqPage } = useFaqSchema()

const maxWidth = computed(() => props.maxWidth ? `${props.maxWidth}px` : null)
const color = computed(() => props.color || null)

type ExtendedDynamicFaqComponent = Omit<ContentSchemas['schemas']['DynamicFaqComponent'], 'faqSections'> & {
    faqSections?: ContentSchemas['schemas']['Faq'][]
}

// There can be multiple FAQ blocks on a page, but only one FAQPage instance is
// allowed in the schema, so every item is collected into a single list.
function collectFaqItems() {
    return (props.content ?? [])
        .filter(child => child.__component === 'dynamic.faq')
        .flatMap(child => (child as ExtendedDynamicFaqComponent).faqSections ?? [])
        .flatMap(section => section?.faqItems ?? [])
}

if (import.meta.server) {
    await addFaqPage(collectFaqItems())
}
</script>

<template>
    <div class="dynamic-content">
        <template v-for="(child, childIndex) in props.content" :key="`${child.__component}${childIndex}`">
            <LazyVisibleNimbusContentFullScreenVideo
                v-if="child.__component === 'dynamic.full-screen-video'"
                :video="child"
                hydrate-on-visible
            />

            <LazyVisibleNimbusContentCenteredText
                v-else-if="child.__component === 'dynamic.centered-text'"
                :centered-text="child"
                hydrate-on-visible
            />

            <div
                v-else
                :class="{ 'dynamic-content--contained': !!maxWidth }"
            >
                <LazyVisibleNimbusContentMediaText
                    v-if="child.__component === 'dynamic.media-text'"
                    :media-text="child"
                    hydrate-on-visible
                />

                <div
                    v-if="child.__component === 'dynamic.rich-text' && child.richtext"
                    class="rich-text"
                    :class="{ 'panel panel__body panel--gray': child.panel }"
                >
                    <LazyNimbusContentBlockRenderer :content="child.richtext as Array<ContentBlock>" hydrate-on-visible />
                </div>

                <LazyVisibleNimbusContentGallery
                    v-if="child.__component === 'dynamic.gallery'"
                    :gallery="child"
                    hydrate-on-visible
                />

                <LazyVisibleNimbusContentPromotion
                    v-if="child.__component === 'dynamic.promotion'"
                    :promotion="child"
                    hydrate-on-visible
                />

                <LazyVisibleNimbusContentGameList
                    v-if="child.__component === 'dynamic.game-list'"
                    :game-list-key="(child.gameList as ContentSchemas['schemas']['GameList'])?.key || 'key-not-set'"
                    :game-list="child.gameList as ContentSchemas['schemas']['GameList']"
                    hydrate-on-visible
                />

                <LazyVisibleNimbusContentWinnersSidebar
                    v-if="child.__component === 'dynamic.winners-sidebar'"
                    :winners-sidebar-key="(child.winnersSidebar as ContentSchemas['schemas']['WinnersSidebar'])?.key || 'key-not-set'"
                    :winners-sidebar="child.winnersSidebar as ContentSchemas['schemas']['WinnersSidebar']"
                    hydrate-on-visible
                />

                <LazyVisibleNimbusContentVendorGames
                    v-if="child.__component === 'dynamic.vendor-list'"
                    :vendor-list="child.vendorList as unknown as ContentVendorListData"
                    static
                    hydrate-on-visible
                />

                <LazyVisibleNimbusContentUspBar
                    v-if="child.__component === 'shared.header' && child.showUspBar"
                    :usps="child.usp"
                    :title-tag="child.uspTitleTag"
                    hydrate-on-visible
                />

                <LazyVisibleNimbusContentTextBlock
                    v-if="child.__component === 'dynamic.text-block'"
                    :text-block="child"
                    hydrate-on-visible
                />

                <LazyVisibleNimbusContentBlogPosts
                    v-if="child.__component === 'dynamic.blog-posts'"
                    :blog-posts="child"
                    hydrate-on-visible
                />

                <template v-if="child.__component === 'dynamic.faq'">
                    <LazyVisibleNimbusContentFaq :sections="child.faqSections as ContentSchemas['schemas']['Faq'][]" hydrate-on-visible />
                </template>

                <template v-if="child.__component === 'dynamic.content-placeholder' && child.contentSlug">
                    <slot :name="child.contentSlug" :block="child" />
                </template>
            </div>
        </template>
    </div>
</template>

<style scoped lang="scss">
.dynamic-content {
    color: v-bind(color);

    > div:not(.panel__body) {
        padding-top: 24px;
        padding-bottom: 24px;
    }

    > .faq {
        padding: 4px 0;
    }

    &--contained {
        max-width: v-bind(maxWidth);
        margin: 0 auto;
    }
}
</style>
